import React from "react";
import { Map } from "immutable";
import { Action } from "typescript-fsa";
import { Button, ButtonProps } from "reactstrap";
import { FormattedMessage } from "react-intl";

import { StateError } from "@orm/models";
import { Spinner } from "@ui/Spinner";
import { ApiRequest } from "../../requests";

type ActionCreator = () => Promise<Action<ApiRequest>> | Action<ApiRequest>;

export interface ApiButtonStateProps {
    requests: Map<string, ApiRequest>;
}

export interface ApiButtonDispatchProps {
    dispatchActionCreators: ActionCreator[];
}

export interface ApiButtonOwnProps {
    actionCreators: ActionCreator[];
    disabled?: boolean;
    color?: string;
    noOutline?: boolean;
    className?: string;
    children?: React.ReactNode;
    onError?: (i: number, error: StateError) => void;
    onSuccess?: (i: number, response: any) => void;
    tag?: React.ElementType;
}

export type ApiButtonProps = ApiButtonOwnProps & ApiButtonDispatchProps & ApiButtonStateProps;

export interface ApiButtonState {
    loading: boolean;
    finished: boolean[];
    currentRequests: ApiRequest[];
}

export class ApiButton extends React.Component<ApiButtonProps, ApiButtonState> {
    constructor(props: ApiButtonProps) {
        super(props);

        // Initialise the state.
        this.state = {
            currentRequests: [],
            loading: false,
            finished: [],
        };
    }

    public componentWillReceiveProps(nextProps: ApiButtonProps) {
        // Check if one of the requests has updated
        const { currentRequests, finished } = this.state;
        const { onSuccess, onError } = this.props;
        let atLeastOneLoading = false;

        for (let i = 0; i < currentRequests.length; i++) {
            const currentRequest = currentRequests[i];

            const nextRequest = nextProps.requests.get(currentRequest.uuid);
            // If the request exists.
            if (nextRequest != null) {
                // Update the requests with this one.
                currentRequests[i] = nextRequest;
                // Set loading if in progress.
                if (nextRequest.isInProgress()) {
                    atLeastOneLoading = true;
                }
                else if (nextRequest.isFinishedSucceeded() && onSuccess != null && !finished[i]) {
                    onSuccess(i, nextRequest.response);
                    finished[i] = true;
                }
                else if (nextRequest.isFinishedFailed() && onError != null && !finished[i]) {
                    onError(i, nextRequest.error);
                    finished[i] = true;
                }
            }
        }

        this.setState({
            currentRequests,
            loading: atLeastOneLoading,
            finished,
        });
    }

    private onClick(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        const { dispatchActionCreators } = this.props;
        const { currentRequests, loading } = this.state;
        let requestStarted = false;
        if (!loading && dispatchActionCreators.length > 0) {
            // Start the requests.
            for (let i = 0; i < dispatchActionCreators.length; i++) {
                const action = dispatchActionCreators[i]();
                if (action instanceof Promise) {
                    this.handlePromise(action, Number(i));
                }
                else if (action.payload instanceof ApiRequest) {
                    requestStarted = true;
                    currentRequests[i] = action.payload;
                }
            }

            // Update the state
            if (requestStarted) {
                this.setState({
                    currentRequests,
                    loading: true,
                    finished: new Array(dispatchActionCreators.length).fill(false),
                });
            }
        }
    }

    private handlePromise(promise: Promise<Action<ApiRequest>>, index: number) {
        promise.then((action: Action<ApiRequest>) => {
            const { currentRequests } = this.state;
            currentRequests[index] = action.payload;

            // Update the state
            this.setState({
                currentRequests,
                loading: true,
            });
        })
        .catch(() => {
            // do nothing with rejected promises.
        });
    }

    public render() {
        let contents = this.props.children;
        const { color, noOutline, disabled, className, tag } = this.props;
        const Tag = tag || Button;

        // Determine the button props
        const buttonProps: ButtonProps = {
            onClick: this.onClick.bind(this),
            disabled: disabled == null ? false : disabled,
            tag: "a",
            outline: true,
            className: className == null ? "" : className,
        };

        if (color != null) {
            buttonProps.color = color;
        }
        if (noOutline != null) {
            buttonProps.outline = !noOutline;
        }

        if (this.state.loading) {
            buttonProps.disabled = true;
            contents = (
                <span>
                    <Spinner inline color={noOutline ? "light" : color} />
                    <FormattedMessage id="ui.async.loading"
                        defaultMessage="Loading" />
                </span>
            );
        }

        return (
            <Tag {...buttonProps} href="#">
                {contents}
            </Tag>
        );
    }
}
